import { FaultData } from "@fairfleet/geotab/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";

/**
 * Hook to fetch the latest fault data for multiple devices within a date range.
 * Supports optional fault source filtering, a result limit, and active fault filtering.
 * Fault data is aggregated by unique device-diagnostic combinations and refetched every 30 seconds.
 *
 * @param deviceIds - Array of device IDs to fetch fault data for.
 * @param fromDate - Start date for the fault data query.
 * @param toDate - End date for the fault data query.
 * @param faultSources - Optional array of fault source IDs to filter results.
 * @param limit - Optional page size for the paginated feed (default is 50000).
 * @param onlyActive - Whether to fetch only active faults (default is true).
 *
 * @returns Aggregated fault data as an array of FaultData.
 */
export function useLatestFaultData(
  deviceIds: string[],
  fromDate: Date,
  toDate: Date,
  faultSources?: string[],
  limit?: number,
  onlyActive = true,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["faultData", activeSessionId, deviceIds, fromDate, toDate, faultSources, onlyActive],
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
    enabled: deviceIds?.length > 0,
    queryFn: async ({ signal }): Promise<FaultData[]> => {
      const results = await Promise.all(
        ((faultSources?.length ?? 0) > 0 ? (faultSources ?? []) : [null]).map(async sourceId => {
          const searchParams = {
            fromDate,
            toDate,
            deviceSearch: { deviceIds },
            ...(sourceId !== null && {
              diagnosticSearch: {
                sourceSearch: { id: sourceId },
              },
            }),
            ...(onlyActive && { state: "Active" }),
          };

          try {
            const faults = await geotabClient.get(
              "FaultData",
              searchParams,
              limit ?? 50000,
              signal,
            );
            return faults || [];
          } catch {
            return [];
          }
        }),
      );

      // Combine results from all fault sources
      const aggregatedFaults = results.flat().reduce((acc: { [key: string]: FaultData }, fault) => {
        const uniqueKey = `${fault.device.id}-${fault.diagnostic.id}`; // Unique key per device-diagnostic pair

        if (!acc[uniqueKey]) {
          acc[uniqueKey] = { ...fault, count: 0 };
        }

        acc[uniqueKey].dateTime =
          acc[uniqueKey].dateTime > fault.dateTime ? acc[uniqueKey].dateTime : fault.dateTime;
        acc[uniqueKey].count += fault.count;

        return acc;
      }, {});

      return Object.values(aggregatedFaults ?? {});
    },
  });
}
